"use client"

import { SearchBar } from "@/components/ui-library-new/search-bar/SearchBar"
import { Button } from "@/components/ui-library/button/Button"
import { VACCINE_TYPE_PARAM } from "@/consts/query-params"
import { BlokProps } from "@/helpers/StoryblokHelpers"
import { SendCircleIcon } from "@/layout/icons/SendCircleIcon"
import type { VaccinationClinicsOverviewSectionStoryblok } from "@/types/component-types"
import { filterBySearchQuery } from "@/utils/filterBySearchQuery"
import { cn, getHaversineDistance } from "@/utils/utils"
import { SbBlokData, storyblokEditable } from "@storyblok/react/rsc"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { useEffect, useMemo, useRef, useState } from "react"
import { FiChevronDown } from "react-icons/fi"
import { VaccinationClinicCard, type ClinicStory } from "./VaccinationClinicCard"

// GeolocationPositionError codes are 1/2/3. Each needs its own wording: "denied" is something the
// user can fix in browser settings, the other two are worth retrying.
const geolocationErrorMessage = (error: GeolocationPositionError) => {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return "Du behöver tillåta platsåtkomst i webbläsaren för att söka närmast dig."
    case error.POSITION_UNAVAILABLE:
      return "Din position kunde inte hämtas. Försök igen eller sök på ort."
    case error.TIMEOUT:
      return "Det tog för lång tid att hämta din position. Försök igen."
    default:
      return "Din position kunde inte hämtas. Sök på ort eller adress istället."
  }
}

// Storyblok number fields come back as strings once an editor has saved them, so coerce
// rather than trusting the generated `number` type. Unset coordinates rank last.
const clinicDistanceKm = (story: ClinicStory, userLat: number, userLng: number) => {
  const lat = Number(story.content.latitude)
  const lng = Number(story.content.longitude)
  if (Number.isNaN(lat) || Number.isNaN(lng)) return Infinity
  return getHaversineDistance(userLat, userLng, lat, lng)
}

const showMoreLessButtonClasses =
  "pointer-events-auto flex items-center gap-[4px] py-2 text-[15px] font-semibold leading-normal tracking-normal text-neutral-90 hover:opacity-75 lg:text-xl lg:font-bold"

type VaccinationClinicsOverviewSectionProps = BlokProps<VaccinationClinicsOverviewSectionStoryblok> & {
  /** Slug -> display name from the "vaccine-types" datasource, fetched server-side by the page. */
  vaccineTypeNames?: Record<string, string>
}

export const VaccinationClinicsOverviewSection = ({
  blok,
  vaccineTypeNames
}: VaccinationClinicsOverviewSectionProps) => {
  const { title, search_placeholder, clinics_list_title, items, find_clinics_radius, search_criterias } = blok

  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()

  const [query, setQuery] = useState("")
  // When non-null, the grid shows these (nearest-first) instead of the search results.
  const [nearestClinics, setNearestClinics] = useState<ClinicStory[] | null>(null)
  // Geolocation can fail in several ways and can take seconds. Both states are surfaced to the
  // user — without them, pressing the button just does nothing visible and reads as broken.
  const [isLocating, setIsLocating] = useState(false)
  const [geoError, setGeoError] = useState<string | null>(null)
  const initialCount = 8
  const [page, setPage] = useState<number>(1)
  const toggleRef = useRef<HTMLDivElement>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [hasCollapsed, setHasCollapsed] = useState(false)

  useEffect(() => {
    if (!hasCollapsed) return

    // Collapsing deletes cards above the fold, so the browser keeps its old offset and the controls
    // drop out of view — the click then reads as "nothing happened". Land on the toggle row itself
    // at both breakpoints: desktop used to scroll the first card of the removed batch to the top,
    // which overshot far enough upward that "Visa Färre" left the viewport entirely.
    toggleRef.current?.scrollIntoView({ behavior: "smooth", block: "end" })
    setHasCollapsed(false)
  }, [hasCollapsed])

  const handleFindNearest = () => {
    setIsExpanded(false)
    setPage(1)
    setGeoError(null)

    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setGeoError("Din webbläsare stöder inte platsdelning. Sök på ort eller adress istället.")
      return
    }

    setIsLocating(true)
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const radiusKm = Number(find_clinics_radius) || 50
        const ranked = clinics
          .map(clinic => ({ clinic, distance: clinicDistanceKm(clinic, coords.latitude, coords.longitude) }))
          .sort((a, b) => a.distance - b.distance)
        const withinRadius = ranked.filter(entry => entry.distance <= radiusKm)
        // Never show an empty screen: if none are in range, fall back to the single closest.
        const result = (withinRadius.length ? withinRadius : ranked.slice(0, 1)).map(entry => entry.clinic)
        setIsLocating(false)
        setQuery("") // "nearest" is its own mode — drop any active text search
        setNearestClinics(result)
      },
      error => {
        setIsLocating(false)
        setGeoError(geolocationErrorMessage(error))
      }
    )
  }

  // Drops only the vaccine param, so any other query state on the URL survives.
  const clearVaccineFilter = () => {
    const params = new URLSearchParams(searchParams.toString())
    params.delete(VACCINE_TYPE_PARAM)
    const rest = params.toString()
    router.replace(rest ? `${pathname}?${rest}` : pathname, { scroll: false })
  }

  const handleToggle = (action: string) => {
    if (!isExpanded) setIsExpanded(true)

    if (action === "more" && page < lastPage) {
      setPage(prev => Math.min(prev + 1, lastPage))
      return
    }

    if (action === "less" && page > 1) {
      setPage(Math.max(page - 1, 1))
      setHasCollapsed(true)
      return
    }
  }

  const clinics = useMemo(() => (items ?? []).filter((item): item is ClinicStory => typeof item !== "string"), [items])
  const unresolvedCount = (items?.length ?? 0) - clinics.length
  const searchCriterias = search_criterias.map(criteria => criteria.toLowerCase())
  const vaccineFilter = searchParams.get(VACCINE_TYPE_PARAM)

  const filteredItems = useMemo(() => {
    // `offered_vaccines` is required in the schema, but a story saved before the field existed
    // would still crash the whole section here — so treat a missing list as "offers nothing".
    const clinicsFilteredByVaccineType = vaccineFilter
      ? clinics.filter(clinic => clinic.content.offered_vaccines?.includes(vaccineFilter))
      : clinics

    return filterBySearchQuery(clinicsFilteredByVaccineType, query, ({ content }) => {
      const parsedContent = Object.entries(content).reduce((acc, [key, value]) => {
        if (searchCriterias.includes(key.toLowerCase())) {
          acc.push(value)
        }
        return acc
      }, [] as unknown[])

      return parsedContent.filter(Boolean).join(" ")
    })
  }, [clinics, query, vaccineFilter, searchCriterias])

  const visibleItems = nearestClinics ?? filteredItems

  const lastPage = Math.ceil(visibleItems.length / initialCount)
  // Clamped: a page's worth is only ever a claim about how many cards exist. A search matching one
  // clinic used to read "Visar 8 av 1", and the last page overcounted whenever it was partly full.
  const expandedCount = Math.min(page * initialCount, visibleItems.length)
  const safePlaceholder = search_placeholder?.trim() || "Sök Mottagningar"

  return (
    <section {...storyblokEditable(blok as SbBlokData)} className="bg-surface pb-[18px] pt-0 lg:pb-10">
      <div className="mx-auto w-full max-w-[1155px] px-4 lg:px-8">
        <h2 className="text-[18px] font-extrabold text-neutral-90 lg:text-2xl">{title}</h2>

        <SearchBar
          value={query}
          onChange={value => {
            setQuery(value)
            setNearestClinics(null) // typing returns to normal search mode
            setGeoError(null) // a stale location error shouldn't hang over a text search
            setPage(1)
            setIsExpanded(false)
          }}
          placeholder={safePlaceholder}
          className="mt-2 lg:mt-4"
        >
          <Button
            type="button"
            variant="tertiary"
            onClick={handleFindNearest}
            disabled={isLocating}
            aria-busy={isLocating}
            className="flex w-full items-center justify-between gap-2 rounded-lg px-0 py-3 text-base text-neutral-50 hover:text-primary-50 disabled:opacity-60 lg:px-6 lg:py-4"
          >
            <span className="hidden text-base lg:inline">{isLocating ? "Söker…" : "Sök närmast mig"}</span>
            <SendCircleIcon className={`h-[21px] w-[21px] lg:h-7 lg:w-7 ${isLocating ? "animate-pulse" : ""}`} />
          </Button>
        </SearchBar>
        {/* Shown while searching too — the running count is how a reader tells a narrow search from a
            failed one. The "nearest" and vaccine-filter modes are excluded because each prints its
            own count below, and two disagreeing totals read as a bug. */}
        {!nearestClinics && !vaccineFilter && visibleItems.length > 0 && (
          <div className="mt-6 text-sm text-neutral-50 lg:mt-8 lg:text-base">
            Visar {expandedCount} av {visibleItems.length}
          </div>
        )}

        {/* Always mounted, will collapse to 0px height when geoError is empty */}
        <p
          role="alert"
          className={cn("text-[15px] leading-[22px] text-warning-50 lg:text-[16px]", geoError && "mt-2 lg:mt-3")}
        >
          {geoError}
        </p>

        {nearestClinics && (
          <div className="mt-6 flex flex-wrap items-center justify-between gap-2 lg:mt-8">
            <span className="text-sm text-neutral-50 lg:text-base">Närmast dig ({nearestClinics.length})</span>
            <button
              type="button"
              onClick={() => setNearestClinics(null)}
              className="text-sm font-semibold text-primary-50 underline underline-offset-2 hover:text-primary-70 lg:text-base"
            >
              Visa alla
            </button>
          </div>
        )}

        {vaccineFilter && !nearestClinics && (
          <div className="mt-6 flex flex-wrap items-center justify-between gap-2 lg:mt-8">
            <span className="text-sm text-neutral-50 lg:text-base">
              Mottagningar som erbjuder {vaccineTypeNames?.[vaccineFilter] ?? vaccineFilter} ({filteredItems.length})
            </span>
            <button
              type="button"
              onClick={clearVaccineFilter}
              className="text-sm font-semibold text-primary-50 underline underline-offset-2 hover:text-primary-70 lg:text-base"
            >
              Visa alla
            </button>
          </div>
        )}

        {/* One grid that simply grows. The container is never animated — see handleToggle. */}
        <div
          className={`grid grid-cols-1 items-start gap-2 lg:gap-4 xl:grid-cols-2 xl:gap-6 ${nearestClinics ? "mt-4" : "mt-4 2xl:mt-12"}`}
        >
          {visibleItems.length === 0 && (
            <p className="col-span-full text-center text-[15px] font-normal leading-[22px] tracking-normal text-neutral-50 lg:text-[16px]">
              {unresolvedCount > 0 ? "Mottagningarna kunde inte laddas." : "Inga mottagningar matchar din sökning."}
            </p>
          )}
          {clinics_list_title && (
            <h3 className="col-span-full mt-8 text-[16px] font-semibold leading-[22px] tracking-[-0.045em] text-neutral-90 lg:mt-12 lg:text-[20px] lg:leading-[27px]">
              {clinics_list_title}
            </h3>
          )}
          {visibleItems.slice(0, expandedCount).map((item, index) => {
            const isRevealed = index >= initialCount
            return (
              <div
                key={item.uuid}
                className={cn(isRevealed && "animate-riseIn")}
                // start each cards anim 40ms apart
                style={isRevealed ? { animationDelay: `${(index % initialCount) * 40}ms` } : undefined}
              >
                <VaccinationClinicCard story={item} />
              </div>
            )
          })}
        </div>

        <div ref={toggleRef} className="mt-2 flex scroll-mb-4 justify-center gap-4 lg:mt-10 lg:scroll-mb-8">
          {page > 1 && isExpanded && (
            <button type="button" onClick={() => handleToggle("less")} className={showMoreLessButtonClasses}>
              Visa Färre
              <FiChevronDown className="h-[25px] w-[25px] rotate-180 text-[#6B6F67] transition-transform duration-300 lg:h-8 lg:w-8" />
            </button>
          )}

          {page < lastPage && (
            <button type="button" onClick={() => handleToggle("more")} className={showMoreLessButtonClasses}>
              Visa Fler
              <FiChevronDown className="h-[25px] w-[25px] text-[#6B6F67] transition-transform duration-300 lg:h-8 lg:w-8" />
            </button>
          )}
        </div>
      </div>
    </section>
  )
}
